<template>
  <div
    class="flex w-full flex-col gap-y-4 px-6 py-4"
    data-testid="rx-step-extras"
  >
    <SupportHeadline
      v-if="isOverlayAvailable"
      class="my-4"
      :text="linkText"
      data-testid="rx-extras-overlay"
      @click="showOverlay"
    />
    <template
      v-for="tintingType in tintingTypes"
      :key="`Color-Option-${tintingType.productId}`"
    >
      <RxSelectOption
        :title="tintingType.title"
        :description="
          tintingType.description +
          (isSunglass && isSunglassTintingOption(tintingType)
            ? `<br/>${$t('rx.sunglass_tinting_hint')}`
            : '')
        "
        :data-testid="`rx-extras-${tintingType.referenceKey}`"
        :show-price="!isClearType(tintingType.referenceKey)"
        :price="tintingType.price"
        :selected="
          tintingType.values.some((i) => i.referenceKey === selectedTintingType)
        "
        @click:select="selectOption(tintingType.values[0])"
      >
        <div
          v-if="tintingTypeHasColorOptions(tintingType)"
          class="flex gap-4 px-6 pb-4"
        >
          <ColorSwatch
            v-for="color in tintingType.values"
            :key="color.variantId"
            :color="color.color"
            :label="color.name"
            :data-testid="`rx-extras-${tintingType.referenceKey}-${color.name}`"
            :selected="
              Boolean(state.tintingType) &&
              selectedTintingType === color.referenceKey
            "
            @click:select.stop="selectOption(color)"
          />
        </div>
        <!-- Highlighted Text -->
        <div
          v-if="isSunglassTintWithSelectedColor(tintingType)"
          class="p-6 pt-0"
        >
          <div class="mb-4 flex gap-2 rounded-md border bg-shade-100 p-2">
            <LazyIconFielmannInfoCircle class="mt-0.5 size-4 shrink-0" />
            <div class="text-sm">
              {{ $t('rxConfigurator.extras.sunglassTintingWarning') }}
            </div>
          </div>
          <!-- VTO Button -->
          <FimButton
            type="secondary"
            class="w-full !whitespace-normal"
            is-small
            data-testid="virtual-try-on-button-rx"
            :disabled="!fitMixReady.value || isVtoOpen.value"
            @click="openVirtualWindow"
          >
            <template #icon>
              <LazyIconFielmannWebcam class="size-4 shrink-0 md:size-6" />
            </template>
            <template v-if="isVtoOpen.value">
              {{ $t('vto.button_in_progress') }}
            </template>
            <template v-else>
              {{ getSelectedColorName(tintingType) }} -
              {{ $t('vto.button_label') }}
            </template>
          </FimButton>
        </div>
      </RxSelectOption>
    </template>
  </div>

  <Overlay
    v-if="isOverlayAvailable"
    :title="overlayTitle"
    :open="isOverlayOpen"
    portal-target="#rx-extras-portal"
    layout="right"
    enter-from="right"
    @update:open="isOverlayOpen = false"
  >
    <div v-for="content in overlayContent" :key="content._uid">
      <component :is="content.component" :blok="content" />
    </div>
  </Overlay>
</template>

<script setup lang="ts">
const emit = defineEmits(['ui:update'])

const $i18n = useI18n()
const { tintingTypes, setTintingType, state } = await useRxConfiguratorState()
const { trackRadioButtonClick } = useTrackingEvents()

const props = defineProps({
  handleVTOOpen: {
    type: Function as PropType<(event: Event) => void>,
    required: true,
  },
  fitMixReady: {
    type: Object as PropType<Ref<boolean>>,
    required: true,
  },
  isVtoOpen: {
    type: Object as PropType<Ref<boolean>>,
    required: true,
  },
})

/** Options */

const firstBasicOption = computed(() =>
  tintingTypes.value.find(({ referenceKey }) => isClearType(referenceKey)),
)

const tintingTypeHasColorOptions = (tintingType: ColorOption) =>
  tintingType.values.length > 1

const isSunglass = computed(() => {
  const product = toValue(state)?.frame?.product
  return product && isGlasses(product)
})

/** Configuration */

const selectedTintingType = ref<ColorOptionValue['referenceKey'] | null>(
  state.value?.tintingType?.variant?.referenceKey || null,
)

const isBasicConfiguration = computed(
  () =>
    !state.value.tintingType ||
    (state.value.tintingType?.variant?.referenceKey &&
      isClearType(state.value.tintingType?.variant?.referenceKey)),
)

const selectOption = (tintingType: ColorOptionValue) => {
  trackRadioButtonClick({
    id: 'radio_lens-tinting',
    label: tintingType.name,
  })
  selectedTintingType.value = tintingType.referenceKey
  setTintingType(tintingType.variantId)
  emit('ui:update', {
    mainAction: 'continue' as RxMainAction,
    buttonLabel: isBasicConfiguration.value
      ? $i18n.t('rxConfigurator.extras.continueWithoutExtras')
      : undefined,
  })
}

const sunglassKeys = [
  rxTintingRefKeys.SUNGLASSES,
  rxTintingRefKeys.ENTRY_NOCOAT_SUNGLASSES,
  rxTintingRefKeys.ENTRY_SUNGLASSES,
  rxTintingRefKeys.PREMIUM_SUNGLASSES,
  rxTintingRefKeys.STANDARD_SUNGLASSES,
  rxTintingRefKeys.STANDARD_PLUS_SUNGLASSES,
]

const isSunglassTintingOption = (option: ColorOption) =>
  sunglassKeys.includes(option.referenceKey)

const selectDefaultOption = () => {
  if (selectedTintingType.value) return

  const sunglassTintingOption = tintingTypes.value.find((option) =>
    isSunglassTintingOption(option),
  )

  if (isSunglass.value && sunglassTintingOption) {
    selectOption(sunglassTintingOption.values[0])
  } else {
    selectOption(
      firstBasicOption.value?.values[0] || tintingTypes.value[0].values[0],
    )
  }
}

onMounted(() => {
  selectDefaultOption()
  emit('ui:update', {
    mainAction: 'continue' as RxMainAction,
    buttonLabel: isBasicConfiguration.value
      ? $i18n.t('rxConfigurator.extras.continueWithoutExtras')
      : undefined,
  })
})

const openVirtualWindow = (event: Event) => {
  event.stopPropagation()
  props.handleVTOOpen(event)
}

/** Overlays */

const {
  overlayTitle,
  overlayContent,
  linkText,
  open: isOverlayOpen,
  show: showOverlay,
  available: isOverlayAvailable,
} = await useOverlay(RxOverlays.TINTING_TYPES)

const getSelectedColorName = (tintingType: ColorOption) => {
  const selectedColor = tintingType.values.find(
    (c) => c.referenceKey === selectedTintingType.value,
  )
  return selectedColor
    ? selectedColor.name
    : $i18n.t('rxConfigurator.extras.noColorSelected')
}

const isSunglassTintWithSelectedColor = (tintingType: ColorOption) => {
  return (
    isSunglass.value &&
    isSunglassTintingOption(tintingType) &&
    tintingType.values.some((i) => i.referenceKey === selectedTintingType.value)
  )
}
</script>
